/**
 * @file src/_modulos/prenota/tabela/layouts/config/hooks/useExportToExcel.ts
 * @description Hook para exportação de dados da tabela para Excel
 * 
 * Princípios aplicados:
 * - SRP: Apenas gerencia exportação para Excel
 * - KISS: Lógica simples e direta
 * - YAGNI: Funcionalidades essenciais apenas
 */

"use client";

import { useCallback } from "react";
import ExcelJS from "exceljs";
import { toast } from "react-hot-toast";
import type { PrenotaItem } from "@apiNf/list";
import { fmt } from "visuais/fmt";
import { buildApiPayload, useTableStore } from "@PN/tabela";

/* -------------------------------------------------------------------------- */
/* TIPOS                                                                      */
/* -------------------------------------------------------------------------- */

export interface ExportOptions {
  filename?: string;
  sheetName?: string;
  includeHeaders?: boolean;
}

export interface ExportData {
  data: PrenotaItem[];
  filters?: Record<string, any>;
  sorting?: Array<{ id: string; desc: boolean }>;
}

/* -------------------------------------------------------------------------- */
/* UTILITÁRIOS                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Mapeia os campos da pré-nota para nomes mais legíveis
 */
const FIELD_MAPPING: Record<string, string> = {
  REC: "ID",
  F1_FILIAL: "Filial",
  F1_DOC: "Documento",
  F1_SERIE: "Série",
  F1_COND: "Cond. Pagamento",
  F1_EMISSAO: "Data Emissão",
  F1_DTDIGIT: "Data Digitação",
  Status: "Status",
  F1_XTIPO: "Tipo",
  F1_XPRIOR: "Prioridade",
  F1_CHVNFE: "Chave NFe",
  F1_XUSRRA: "Usuário",
  F1_XOBS: "Observações",
  F1_ZOBSREV: "Obs. Revisão",
  F1_VALBRUT: "Valor Bruto",
  A2_COD: "Cód. Fornecedor",
  A2_LOJA: "Loja Fornecedor",
  A2_CGC: "CNPJ/CPF Fornecedor",
  A2_NOME: "Nome Fornecedor",
  VENCIMENTO: "Vencimento",
  Z07_CHAVE: "Tem Anexos",
};

/**
 * Formata os dados para exportação
 */
function countAttachments(value?: string): number {
  if (!value) return 0;
  const trimmed = value.trim();
  if (!trimmed) return 0;
  
  // Uma chave de anexo tem formato: "0108000001810U  06735601" (com espaço interno)
  const multipleKeysPattern = /[,;]/;
  
  if (multipleKeysPattern.test(trimmed)) {
    // Se tem separadores, conta as partes
    const parts = trimmed.split(/[,;]/).filter(part => part.trim().length > 0);
    return parts.length;
  } else {
    // Se não tem separadores, verifica se é uma chave válida (não vazia)
    return trimmed.length > 0 ? 1 : 0;
  }
}

function formatDataForExport(data: PrenotaItem[]): any[] {
  return data.map((item) => {
    const formattedItem: any = {};

    // Exporta APENAS os campos definidos em FIELD_MAPPING (whitelist)
    Object.entries(FIELD_MAPPING).forEach(([key, displayName]) => {
      const value = (item as any)[key];
      if (value === undefined) return;

      // Formata valores específicos
      if (key === "Z07_CHAVE") {
        formattedItem[displayName] = value ? "Sim" : "Não"; 
        return;
      }

      if (key === "F1_VALBRUT" && typeof value === "number") {
        formattedItem[displayName] = new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(value);
      } else if (key === "VENCIMENTO") {
        // Processa múltiplas datas separadas por vírgula
        const datesStr = String(value || "").split(",").map((d) => d.trim()).filter(Boolean);
        const formattedDates = datesStr.map((dateStr) => {
          return fmt.dates.BR(dateStr as any, { fallback: dateStr });
        });
        formattedItem[displayName] = formattedDates.join(", ");
      } else if (key === "F1_DTDIGIT" || key === "F1_EMISSAO") {
        // Datas no padrão BR usando utilitário central
        formattedItem[displayName] = fmt.dates.BR(value as any, { fallback: "" });
      } else {
        formattedItem[displayName] = value;
      }
    });

    return formattedItem;
  });
}

/**
 * Cria uma aba de informações sobre os filtros aplicados
 */
function createFiltersSheet(filters: Record<string, any>, sorting: Array<{ id: string; desc: boolean }> = []): any[] {
  const filterRows: any[] = [];
  
  // Cabeçalho
  filterRows.push({
    "Tipo": "Filtros e Configurações",
    "Valor": "",
  });
  
  filterRows.push({
    "Tipo": "",
    "Valor": "",
  });
  
  // Filtros aplicados
  if (Object.keys(filters).length > 0) {
    filterRows.push({
      "Tipo": "FILTROS APLICADOS",
      "Valor": "",
    });
    
    Object.entries(filters).forEach(([key, value]) => {
      const displayName = FIELD_MAPPING[key] || key;
      let displayValue = "";
      
      if (Array.isArray(value)) {
        displayValue = value.join(", ");
      } else if (typeof value === "object" && value !== null) {
        if (value.min !== undefined || value.max !== undefined) {
          const min = value.min !== undefined ? new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(value.min) : "∞";
          const max = value.max !== undefined ? new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(value.max) : "∞";
          displayValue = `${min} até ${max}`;
        } else {
          displayValue = JSON.stringify(value);
        }
      } else {
        displayValue = String(value);
      }
      
      filterRows.push({
        "Tipo": displayName,
        "Valor": displayValue,
      });
    });
  } else {
    filterRows.push({
      "Tipo": "FILTROS APLICADOS",
      "Valor": "Nenhum filtro aplicado",
    });
  }
  
  filterRows.push({
    "Tipo": "",
    "Valor": "",
  });
  
  // Ordenação
  if (sorting.length > 0) {
    filterRows.push({
      "Tipo": "ORDENAÇÃO",
      "Valor": "",
    });
    
    sorting.forEach((sort) => {
      const displayName = FIELD_MAPPING[sort.id] || sort.id;
      filterRows.push({
        "Tipo": displayName,
        "Valor": sort.desc ? "Decrescente" : "Crescente",
      });
    });
  } else {
    filterRows.push({
      "Tipo": "ORDENAÇÃO",
      "Valor": "Ordenação padrão",
    });
  }
  
  filterRows.push({
    "Tipo": "",
    "Valor": "",
  });
  
  // Informações da exportação
  filterRows.push({
    "Tipo": "INFORMAÇÕES DA EXPORTAÇÃO",
    "Valor": "",
  });
  
  filterRows.push({
    "Tipo": "Data/Hora",
    "Valor": new Date().toLocaleString("pt-BR"),
  });
  
  filterRows.push({
    "Tipo": "Total de Registros",
    "Valor": filters.totalCount || "N/A",
  });
  
  return filterRows;
}

/* -------------------------------------------------------------------------- */
/* HOOK PRINCIPAL                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Hook para exportação de dados para Excel
 * Busca TODOS os dados filtrados da API antes de exportar
 */
export function useExportToExcel() {
  const { activeTab, appliedFilters, selectedFilials, sorting, searchTerm, searchField } = useTableStore();

  const exportToExcel = useCallback(
    async ({ data, filters = {}, sorting: sortingParam = [] }: ExportData, options: ExportOptions = {}) => {
      try {
        // Mostra loading
        toast.loading("Buscando todos os dados para exportação...", { id: "export-loading" });

        // Constrói payload para buscar TODOS os dados (sem paginação)
        const payload = buildApiPayload(
          appliedFilters,
          activeTab,
          selectedFilials,
          sorting.length > 0 ? sorting : sortingParam,
          1, // page não importa quando fetchAll=true
          10, // pageSize não importa quando fetchAll=true
          searchTerm,
          searchField
        );

        // Faz chamada à API com fetchAll=true para buscar todos os dados
        const response = await fetch("/api/prenotas/list", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...payload,
            fetchAll: true, // Flag para buscar todos os dados
          }),
        });

        if (!response.ok) {
          throw new Error(`Erro ao buscar dados: ${response.statusText}`);
        }

        const result = await response.json();

        if (!result.success || !result.data || result.data.length === 0) {
          toast.error("Nenhum dado encontrado para exportação", { id: "export-loading" });
          return;
        }

        // Usa os dados completos da API
        const allData = result.data as PrenotaItem[];

        toast.dismiss("export-loading");
        toast.loading(`Exportando ${allData.length} registros...`, { id: "export-processing" });

        // Configurações padrão
        const {
          filename = `prenotas_${new Date().toISOString().split("T")[0]}.xlsx`,
          sheetName = "Pré-Notas",
          includeHeaders = true,
        } = options;

        // Formata os dados (usa allData em vez de data)
        const formattedData = formatDataForExport(allData);
        
        // Cria o workbook
        const workbook = new ExcelJS.Workbook();
        
        // Cria a planilha principal
        const worksheet = workbook.addWorksheet(sheetName);
        
        // Adiciona cabeçalhos se solicitado
        if (includeHeaders && formattedData.length > 0) {
          const headers = Object.keys(formattedData[0]);
          worksheet.addRow(headers);
          
          // Formata cabeçalhos
          const headerRow = worksheet.getRow(1);
          headerRow.font = { bold: true };
          headerRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE0E0E0' }
          };
        }
        
        // Adiciona os dados
        formattedData.forEach((row) => {
          worksheet.addRow(Object.values(row));
        });
        
        // Ajusta largura das colunas
        worksheet.columns.forEach((column) => {
          column.width = 15;
        });
        
        // Cria a planilha de filtros
        const filtersWithTotal = {
          ...filters,
          totalCount: result.total ?? allData.length,
        } as Record<string, any>;
        const filtersData = createFiltersSheet(filtersWithTotal, sorting.length > 0 ? sorting : sortingParam);
        
        const filtersWorksheet = workbook.addWorksheet("Filtros");
        
        // Adiciona cabeçalhos da planilha de filtros
        filtersWorksheet.addRow(["Tipo", "Valor"]);
        const filtersHeaderRow = filtersWorksheet.getRow(1);
        filtersHeaderRow.font = { bold: true };
        filtersHeaderRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0E0E0' }
        };
        
        // Adiciona os dados de filtros
        filtersData.forEach((row) => {
          filtersWorksheet.addRow(Object.values(row));
        });
        
        // Ajusta largura das colunas da planilha de filtros
        filtersWorksheet.columns = [
          { width: 25 },
          { width: 40 },
        ];
        
        // Gera o buffer do arquivo
        const buffer = await workbook.xlsx.writeBuffer();
        
        // Cria o blob e faz o download
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // Feedback de sucesso
        toast.dismiss("export-processing");
        toast.success(`Arquivo Excel exportado com sucesso! (${allData.length} registros)`);
        
      } catch (error) {
        console.error("Erro ao exportar para Excel:", error);
        toast.dismiss("export-loading");
        toast.dismiss("export-processing");
        toast.error(error instanceof Error ? error.message : "Erro ao exportar arquivo Excel");
      }
    },
    [activeTab, appliedFilters, selectedFilials, sorting, searchTerm, searchField]
  );

  return {
    exportToExcel,
  };
}
